import React from 'react';

import { Pill, PillVariant } from '@/components/common/Pill';
import { CanonicalTaskStatus } from '@/services/types/task-types';

/**
 * Task status to pill variant mapping
 * Updated with softer, more muted colors per design requirements
 * - IN_PROGRESS, PENDING: Purple/indigo badges (indigo-500)
 * - BLOCKED: Red badge (red-500)
 * - COMPLETED: Green badge (green-500)
 * - NOT_APPLICABLE, CLOSED: Gray badges (gray-300)
 */
const TASK_STATUS_VARIANTS: Record<string, PillVariant> = {
  [CanonicalTaskStatus.IN_PROGRESS]: 'purple',
  [CanonicalTaskStatus.PENDING]: 'purple',
  [CanonicalTaskStatus.BLOCKED]: 'red',
  [CanonicalTaskStatus.COMPLETED]: 'green',
  [CanonicalTaskStatus.NOT_APPLICABLE]: 'gray',
  [CanonicalTaskStatus.CLOSED]: 'gray',
};

const DEFAULT_VARIANT: PillVariant = 'gray';

interface TaskStatusPillProps {
  /** Task status value (e.g., "In Progress", "Pending", "Blocked") */
  status: string;
  /** Size of the pill (default: sm for table usage) */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
}

/**
 * Reusable task status pill component with color-coding
 *
 * Updated to use the Pill component for consistency across the application.
 * Colors are softer and more muted per design requirements.
 * Defaults to 'sm' size for table usage.
 *
 * Usage:
 * ```tsx
 * <TaskStatusPill status={task.status?.canonical_status} />
 * ```
 *
 * Colors:
 * - In Progress / Pending (Requested): Purple (indigo-500)
 * - Blocked: Red (red-500)
 * - Completed: Green (green-500)
 * - Not Applicable / Closed: Gray (gray-300)
 */
export const TaskStatusPill: React.FC<TaskStatusPillProps> = ({
  status,
  size = 'sm',
  className = '',
}) => {
  if (!status) {
    return <span className="text-gray-400 text-sm">—</span>;
  }

  const variant = TASK_STATUS_VARIANTS[status] || DEFAULT_VARIANT;

  return (
    <Pill variant={variant} size={size} className={className}>
      {status}
    </Pill>
  );
};
